import { Box, Hidden, Link, Typography } from '@mui/material';
import Grid from 'components/core/Grid';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import StarIcon from '@mui/icons-material/Star';
import styled from 'styled-components';
import { usePropertyInformationQuery } from 'components/views/pages/Checkout/hooks/usePropertyInformationQuery';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { CheckoutSummaryPhotoGallery } from './CheckoutSummaryPhotoGallery';
import { CheckoutSummaryPropertyReviews } from './CheckoutSummaryPropertyReviews';
import { useToggle } from '@alluradirect/react';

const SummaryImage = styled.div`
  overflow: hidden;
  max-height: 104px;
  border-radius: 5px;
  text-align: center;
  border: 1px solid ${props => props.theme.palette.grey[300]};
  position: relative;

  & img {
    object-fit: contain;
    width: 100%;
    border-radius: 5px;
  }

  ${props => props.theme.breakpoints.down('sm')} {
    max-height: 100px;
  }
  
  &:hover {
    cursor: pointer;
    filter: brightness(1.1);
    transition: all 0.2s;
  }

  &:after {    
    content: "View Gallery";
    font-size: 12px;
    font-weight: 500;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, -6px);
    white-space: nowrap;
    color: #fff;
    opacity: 0.8;
    background-color: rgba(0,0,0,0.8);
    padding: 4px 8px;
    border-radius: 5px;
  }
`;

const PropertyTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SummaryDescription = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledReviewText = styled(Box)`
  align-items: center;
  display: flex;
`;

const StyledStarIcon = styled(StarIcon)`
  color: ${props => props.theme.palette.warning.main};
  margin-right: ${props => props.theme.spacing(0.5)};
  font-size: 14px !important;
  margin-bottom: 2px;
`;

export const CheckoutSummaryProperty = ({ pid }: Pick<CheckoutStepProps, 'pid'>) => {
  const propertyInfo = usePropertyInformationQuery({ pid: Number(pid), enabled: Boolean(pid) });
  const isLoading = !propertyInfo.data;

  const propertyID = propertyInfo.data?.DATA.ID
  const propertyThumbnail = propertyInfo.data?.DATA.THUMBNAIL_URL;
  const propertyNameResort = propertyInfo.data
    ? `${propertyInfo.data.DATA.ADDRESS.RESORT}`
    : '';

  const propertyNameDevelopment = propertyInfo.data
    ? `${propertyInfo.data.DATA.ADDRESS.DEVELOPMENT}`
    : '';

  const propertyTitle = propertyInfo.data?.DATA.TITLE ?? '--';

  const numReviews = propertyInfo.data?.DATA.NUM_REVIEWS ?? 0;
  const averageReviewScore = propertyInfo.data?.DATA.AVERAGE_REVIEW_SCORE ?? 0;
  // const hostedBy = propertyInfo.data?.DATA.HOST_FIRST_NAME;  
  const sizeCategory = propertyInfo.data?.DATA.SIZE_CATEGORY;
  const bathroomCount = propertyInfo.data?.DATA.NUM_BATHS;
  const maxGuestCapacity = propertyInfo.data?.DATA.MAX_CAPACITY;
  const images = propertyInfo.data?.DATA.PHOTOS;

  const togglePhotoGallery = useToggle();
  const togglePropertyReviews = useToggle();

  return (
    <>

      <Grid item xs={12}>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
              <PropertyTitle fontWeight="500" fontSize={20} lineHeight="24px">
                {propertyTitle}
              </PropertyTitle>
            </LoadingSkeleton>
          </Grid>

          <Grid item xs={5} sm={3} md={5} lg={4}>
            <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
              <SummaryImage onClick={togglePhotoGallery.open}>
                <img src={propertyThumbnail} />
              </SummaryImage>
            </LoadingSkeleton>
          </Grid>
          <Grid item xs={7} sm={9} md={7} lg={8} style={{ display: 'flex', paddingBottom: 0 }}>
            <Grid container>
              <Grid item xs={12} mb={0.5}>
                {numReviews > 0 && (
                  <LoadingSkeleton isLoading={isLoading}>
                    <StyledReviewText onClick={togglePropertyReviews.open}>
                      <StyledStarIcon />
                      <Typography variant="body1">
                        <strong>{averageReviewScore}</strong> <Link color="secondary" style={{ cursor: 'pointer' }} >({numReviews} Reviews)</Link>
                      </Typography>
                    </StyledReviewText>
                  </LoadingSkeleton>
                )}
              </Grid>
              <Grid item xs={12}>
                <LoadingSkeleton isLoading={isLoading}>
                  <SummaryDescription variant="body1" lineHeight="18px" fontWeight={500}>
                    {propertyNameResort}
                  </SummaryDescription>
                  <SummaryDescription variant="caption" lineHeight="18px" mb={1}>
                    {propertyNameDevelopment}
                  </SummaryDescription>
                </LoadingSkeleton>
                <LoadingSkeleton isLoading={isLoading}>
                  <SummaryDescription variant="caption" lineHeight="18px">
                    {sizeCategory}, {bathroomCount} Bathroom
                  </SummaryDescription>
                </LoadingSkeleton>
                <Hidden smDown>
                  <LoadingSkeleton isLoading={isLoading}>
                    <SummaryDescription variant="caption" lineHeight="18px">
                      Maximum group size is {maxGuestCapacity} people
                    </SummaryDescription>
                  </LoadingSkeleton>
                </Hidden>
              </Grid>
              {/* 
              <Grid item xs={12}>
                <LoadingSkeleton isLoading={isLoading}>
                  <Typography variant="body2" fontWeight="500" lineHeight="18px">
                    Hosted by {hostedBy}
                  </Typography>
                </LoadingSkeleton>
              </Grid> */}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Hidden mdUp>
        <Grid item xs={12}>
          <AppDivider marginVertical={'4px'} borderWidth='0' />
        </Grid>
      </Hidden>

      {togglePhotoGallery.isOpen ? (
        <CheckoutSummaryPhotoGallery
          isOpen={togglePhotoGallery.isOpen}
          close={togglePhotoGallery.close}
          images={images || []}
        />
      ) : null}

      {togglePropertyReviews.isOpen ? (
        <CheckoutSummaryPropertyReviews
          isOpen={togglePropertyReviews.isOpen}
          close={togglePropertyReviews.close}
          propertyId={propertyID ?? 0}
        />
      ) : null}
    </>
  );
};
